import { useCallback, useEffect, useState } from "react";

import usePaymentProfile from "@/hooks/usePaymentProfile";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from "@/i18n/useTranslations";
import paymentProfileService from "@/services/paymentProfile.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import SettingsLoadingPage from "@/shared-components/loaders/components/SettingsLoadingPage";
import BillingDetailsCard from "@/shared-components/payment-profile/BillingDetailsCard";
import ConfirmationDialog from "@/shared-components/popups/components/ConfirmationDialog";
import SettingsHeading from "@/shared-components/text/components/SettingsHeading";
import Text from "@/shared-components/text/components/Text";

import PaymentMethodCard from "./components/PaymentMethodCard";

import "./index.css";

function pickArray(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data)) {
      return payload.data;
    }
    if (Array.isArray(payload.results)) {
      return payload.results;
    }
    if (Array.isArray(payload.payment_methods)) {
      return payload.payment_methods;
    }
    if (Array.isArray(payload.items)) {
      return payload.items;
    }
  }
  return [];
}

function parseNumeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseTimestamp(value) {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = Number(value);
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  const date = Date.parse(value);
  return Number.isFinite(date) ? date : null;
}

function formatLabel(value) {
  if (!value) {
    return null;
  }
  return String(value)
    .replace(/[_-]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
}

function normalizePaymentMethod(candidate, defaultId) {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const cardDetails =
    candidate.card ||
    candidate.payment_method_details?.card ||
    candidate.payment_method?.card ||
    candidate.details?.card ||
    candidate.billing_details?.card ||
    null;

  const id =
    candidate.id ||
    candidate.payment_method_id ||
    candidate.payment_method ||
    candidate.stripe_payment_method_id ||
    candidate._id ||
    null;

  if (!id) {
    return null;
  }

  const expMonth = parseNumeric(cardDetails?.exp_month ?? candidate.exp_month ?? candidate.card_exp_month);
  const expYear = parseNumeric(cardDetails?.exp_year ?? candidate.exp_year ?? candidate.card_exp_year);
  const countrySource =
    cardDetails?.country ??
    candidate.country ??
    candidate.billing_details?.address?.country ??
    candidate.address?.country ??
    null;

  return {
    id,
    brand: formatLabel(cardDetails?.brand ?? candidate.brand ?? candidate.card_brand),
    last4: String(cardDetails?.last4 ?? candidate.last4 ?? candidate.card_last4 ?? "").slice(-4),
    expMonth,
    expYear,
    funding: formatLabel(cardDetails?.funding ?? candidate.funding ?? candidate.card_funding),
    country: countrySource ? String(countrySource).toUpperCase() : null,
    label: candidate.nickname ?? candidate.label ?? null,
    isDefault: Boolean(
      candidate.is_default ??
      candidate.isDefault ??
      candidate.default ??
      (defaultId ? id === defaultId : false),
    ),
    createdAt: parseTimestamp(
      candidate.created ??
      candidate.created_at ??
      candidate.card_created_at ??
      cardDetails?.created ??
      candidate.updated_at,
    ),
  };
}

function normalizePaymentMethodsPayload(payload) {
  const defaultId =
    payload?.default_payment_method_id ??
    payload?.default_payment_method ??
    payload?.default ??
    payload?.defaultPaymentMethodId ??
    null;

  const normalized = pickArray(payload)
    .map((item) => normalizePaymentMethod(item, defaultId))
    .filter(Boolean);

  const resolvedDefaultId = normalized.find((item) => item.isDefault)?.id ?? defaultId ?? null;

  const items = normalized
    .map((item) => ({
      ...item,
      isDefault: resolvedDefaultId ? item.id === resolvedDefaultId : item.isDefault,
    }))
    .sort((a, b) => {
      if (a.isDefault === b.isDefault) {
        if (a.createdAt && b.createdAt) {
          return b.createdAt - a.createdAt;
        }
        return 0;
      }
      return a.isDefault ? -1 : 1;
    });

  return { items, defaultId: resolvedDefaultId };
}

export default function PaymentMethods() {
  const t = useTranslations('common.user_settings.payment_methods');
  const paymentProfile = usePaymentProfile();
  const ensurePaymentProfile = paymentProfile?.ensurePaymentProfile;
  const refreshPaymentProfile = paymentProfile?.refreshProfile;
  const paymentProfileCountry =
    paymentProfile?.profile?.country ??
    paymentProfile?.profile?.country_code ??
    paymentProfile?.profile?.country_iso ??
    null;
  const { stateHandler } = useStateHandler();

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(null);
  const [addingMethod, setAddingMethod] = useState(false);
  const [updatingDefaultId, setUpdatingDefaultId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [methodPendingRemoval, setMethodPendingRemoval] = useState(null);

  const fetchPaymentMethods = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) {
        setLoading(true);
      }
      setListError(null);
      try {
        const { response, data } = await paymentProfileService.listPaymentMethods();
        if (response?.ok) {
          const normalized = normalizePaymentMethodsPayload(data);
          setPaymentMethods(normalized.items);
        }
        else if (response?.status === 404) {
          setPaymentMethods([]);
        }
        else {
          throw new Error("Unexpected response");
        }
      }
      catch (error) {
        console.error("[PaymentMethods] Failed to load payment methods", error);
        setListError(t('errors.load_failed'));
        setPaymentMethods([]);
      }
      finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [t],
  );

  useEffect(() => {
    fetchPaymentMethods();
  }, [fetchPaymentMethods]);

  const handleAddPaymentMethod = useCallback(async () => {
    if (typeof ensurePaymentProfile !== "function") {
      return;
    }
    setAddingMethod(true);
    try {
      await ensurePaymentProfile({
        forceSetup: true,
        countryHint: paymentProfileCountry ?? undefined,
      });
      await Promise.all([
        fetchPaymentMethods({ silent: true }),
        typeof refreshPaymentProfile === "function" ? refreshPaymentProfile() : Promise.resolve(),
      ]);
      stateHandler.set("temporalPopup", {
        text: t('popups.added'),
      });
    }
    catch (error) {
      if (error?.code !== "BIDDER_PROFILE_CANCELLED") {
        console.error("[PaymentMethods] Unable to add payment method", error);
        stateHandler.set("temporalPopup", {
          text: t('popups.add_failed'),
        });
      }
    }
    finally {
      setAddingMethod(false);
    }
  }, [ensurePaymentProfile, paymentProfileCountry, fetchPaymentMethods, refreshPaymentProfile, stateHandler, t]);

  const handleSetDefault = useCallback(
    async (methodId) => {
      if (!methodId) {
        return;
      }
      setUpdatingDefaultId(methodId);
      try {
        const { response } = await paymentProfileService.setDefaultPaymentMethod(methodId);
        if (!response?.ok && response?.status !== 204) {
          throw new Error("Unable to set default payment method.");
        }

        setPaymentMethods((prevMethods) =>
          prevMethods.map((method) => ({
            ...method,
            isDefault: method.id === methodId,
          })),
        );

        if (typeof refreshPaymentProfile === "function") {
          refreshPaymentProfile();
        }

        stateHandler.set("temporalPopup", {
          text: t('popups.default_updated'),
        });
      }
      catch (error) {
        console.error("[PaymentMethods] Failed to set default method", error);
        stateHandler.set("temporalPopup", {
          text: t('popups.default_update_failed'),
        });
      }
      finally {
        setUpdatingDefaultId(null);
      }
    },
    [refreshPaymentProfile, stateHandler, t],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!methodPendingRemoval?.id) {
      return;
    }
    const methodId = methodPendingRemoval.id;
    setDeletingId(methodId);
    try {
      const { response } = await paymentProfileService.deletePaymentMethod(methodId);
      if (!response?.ok && response?.status !== 204 && response?.status !== 404) {
        throw new Error("Unable to delete payment method.");
      }

      setPaymentMethods((prevMethods) => prevMethods.filter((method) => method.id !== methodId));
      await fetchPaymentMethods({ silent: true });

      if (typeof refreshPaymentProfile === "function") {
        refreshPaymentProfile();
      }

      stateHandler.set("temporalPopup", {
        text: t('popups.removed'),
      });
    }
    catch (error) {
      console.error("[PaymentMethods] Failed to delete method", error);
      stateHandler.set("temporalPopup", {
        text: t('popups.remove_failed'),
      });
    }
    finally {
      setDeletingId(null);
      setMethodPendingRemoval(null);
    }
  }, [fetchPaymentMethods, methodPendingRemoval, refreshPaymentProfile, stateHandler, t]);

  if (loading) {
    return <SettingsLoadingPage page={t('heading')} />;
  }

  const hasPaymentMethods = paymentMethods.length > 0;
  const canAddMethod = typeof ensurePaymentProfile === "function";

  return (
    <>
      <SettingsHeading text={t('heading')} />
      <div className="settings-payment-methods__container">
        <div className="add-billing-card settings-payment-methods__payment-card">
          <div className="add-billing-card__header">
            <div>
              <div className="add-billing-card__title-row">
                <h3 className="add-billing-card__title">{t('section_title')}</h3>
              </div>
            </div>

            {(hasPaymentMethods || listError) && (
              <ContinueButton
                type="button"
                className="add-billing-card__action"
                onClick={handleAddPaymentMethod}
                loading={addingMethod}
                turnOff={!canAddMethod}
              >
                {t('buttons.add')}
              </ContinueButton>
            )}
          </div>

          {!hasPaymentMethods && !listError ? (
            <div className="add-billing-card__empty-state">
              <p className="add-billing-card__empty">
                {t('empty_state')}
              </p>
              <ContinueButton
                type="button"
                className="add-billing-card__action"
                onClick={handleAddPaymentMethod}
                loading={addingMethod}
                turnOff={!canAddMethod}
              >
                {t('buttons.add')}
              </ContinueButton>
            </div>
          ) : null}

          {hasPaymentMethods ? (
            <div className="settings-payment-methods__list">
              {paymentMethods.map((method) => (
                <PaymentMethodCard
                  key={method.id}
                  method={method}
                  onSetDefault={() => handleSetDefault(method.id)}
                  onDelete={() => setMethodPendingRemoval(method)}
                  isUpdating={Boolean(updatingDefaultId)}
                  isDeleting={deletingId === method.id}
                />
              ))}
            </div>
          ) : null}
        </div>

        <BillingDetailsCard className="settings-payment-methods__billing-card" hideInfoIcon />

        {listError && (
          <div className="settings-payment-methods__error">
            <Text paragraph className="font-size-small">
              {listError}
            </Text>
            <button
              type="button"
              className="settings-payment-methods__retry"
              onClick={() => fetchPaymentMethods()}
            >
              {t('buttons.try_again')}
            </button>
          </div>
        )}

      </div>
      <ConfirmationDialog
        open={Boolean(methodPendingRemoval)}
        onClose={() => setMethodPendingRemoval(null)}
        onCloseAction={() => setMethodPendingRemoval(null)}
        onConfirm={handleConfirmDelete}
        title={t('remove_dialog.title')}
        dialogText={t('remove_dialog.message')}
        confirmButtonText={t('remove_dialog.confirm_button')}
        showCancelButton={false}
        loading={Boolean(deletingId)}
        opacity
      />
    </>
  );
}
